import { useState } from "react";
import { toast } from "sonner";
import { Smartphone, KeyRound, X, UserRound } from "lucide-react";
import { api } from "@/lib/api";
import { ClientTag } from "@/components/chat/ClientTag";
import { MacReads } from "@/components/chat/MacReads";
import { AppConfigs } from "@/components/chat/AppConfigs";
import { WaBillingButton } from "@/components/chat/WaBillingButton";

const Section = ({ title, children, testid }) => (
  <section data-testid={testid} className="border-b border-[#1F293D] px-3 py-3">
    <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">{title}</div>
    {children}
  </section>
);

const ClientActions = ({ s, reload, openClient }) => {
  const [busy, setBusy] = useState(false);
  const run = async (msg, fn, ok) => {
    if (!window.confirm(msg)) return;
    setBusy(true);
    try { await ok(await fn()); reload(); } catch (e) { toast.error(e.response?.data?.detail || "Falha ao enviar"); }
    setBusy(false);
  };
  const appBilling = () => run(`Enviar a cobrança com o link PIX para ${s.client.name} aqui no chat do app?`,
    () => api.post(`/clients/${s.client_id}/remind`, { channel: "app" }),
    ({ data }) => { const r = data.results?.[0]; r?.status === "sent" ? toast.success(r.push ? "Cobrança enviada no app + aviso no celular" : "Cobrança enviada no chat") : toast.warning(r?.error || "Envio ignorado"); });
  const info = () => run(`Enviar usuário, SENHA, plano e link PIX de ${s.client.name} nesta conversa?\n\nConfirme que quem está conversando é mesmo o cliente.`,
    () => api.post(`/clients/${s.client_id}/app-info`), () => toast.success("Dados de acesso enviados"));
  const btn = "inline-flex w-full items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-bold transition-opacity hover:opacity-90 disabled:opacity-50";
  return (
    <div className="space-y-2">
      <ClientTag c={s.client} big testid="chat-thread-client-tag" />
      <button data-testid="chat-side-open-client" onClick={() => openClient(s.client_id)} className={`${btn} border border-[#1F293D] text-slate-200`}><UserRound className="h-3.5 w-3.5" /> Abrir ficha</button>
      <button data-testid="chat-send-app-billing" onClick={appBilling} disabled={busy} className={`${btn} bg-[#D4FF3A] text-slate-950`}><Smartphone className="h-3.5 w-3.5" /> Cobrar pelo app</button>
      <WaBillingButton s={s} disabled={busy} setBusy={setBusy} onSent={reload} />
      <button data-testid="chat-send-access-info" onClick={info} disabled={busy} className={`${btn} border border-[#D4FF3A]/50 text-[#D4FF3A]`}><KeyRound className="h-3.5 w-3.5" /> Enviar dados de acesso</button>
    </div>
  );
};

export const sideCount = (s) => new Set((s.mac_reads || []).filter((r) => r.mac).map((r) => r.mac)).size + (s.app_configs || []).length;

export const ChatSidePanel = ({ s, open, wide, onClose, reload, onLinked, openClient }) => {
  if (!open) return null;
  const empty = !s.client && !sideCount(s) && !s.mac_alert;
  return (
    <>
      <div onClick={onClose} className={`absolute inset-0 z-10 bg-black/40 ${wide ? "xl:hidden" : ""}`} />
      <aside data-testid="chat-side-panel"
        className={`absolute inset-y-0 right-0 z-20 flex w-[330px] max-w-[92%] flex-col overflow-y-auto border-l border-[#1F293D] bg-[#0F141F] shadow-2xl ${wide ? "xl:static xl:z-auto xl:max-w-none xl:shadow-none" : ""}`}>
        <div className="flex items-center justify-between border-b border-[#1F293D] px-3 py-2.5">
          <span className="text-sm font-bold text-white">Detalhes</span>
          <button data-testid="chat-side-close" onClick={onClose} title="Recolher" className="rounded-md p-1 text-slate-400 transition-colors hover:bg-[#1A2234] hover:text-white"><X className="h-4 w-4" /></button>
        </div>
        {s.client && <Section title="Cliente" testid="chat-side-client"><ClientActions s={s} reload={reload} openClient={openClient} /></Section>}
        {(sideCount(s) > 0 || s.mac_alert) && (
          <Section title="MACs e apps" testid="chat-side-macs">
            <div className="space-y-2">
              <MacReads s={s} onLinked={onLinked} openClient={openClient} />
              <AppConfigs s={s} reload={reload} />
            </div>
          </Section>
        )}
        {empty && <div className="px-4 py-8 text-center text-xs text-slate-500">Ainda sem ficha, MAC ou app nesta conversa.</div>}
      </aside>
    </>
  );
};
